import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, getRevenueCatProjectId } from './firebase';
import {
  RevenueCatOfferingsResponse,
  RevenueCatSubscriber,
} from '../types/revenuecat';

const functions = getFunctions(app, 'europe-west2');

// Initialize/Login user in RevenueCat using Firebase UID
export const initializeRevenueCat = async (userId: string, email?: string): Promise<RevenueCatSubscriber | null> => {
  try {
    const fn = httpsCallable<{ userId: string; email?: string }, { subscriber: RevenueCatSubscriber | null }>(
      functions, 'revenueCatInitialize'
    );
    const result = await fn({ userId, email });
    if (result.data.subscriber) {
      console.log('RevenueCat subscriber loaded for user:', userId);
    }
    return result.data.subscriber;
  } catch (error) {
    console.error('Error initializing RevenueCat:', error);
    return null;
  }
};

export const getOfferings = async (userId?: string): Promise<RevenueCatOfferingsResponse | null> => {
  try {
    const projectId = getRevenueCatProjectId();
    if (!projectId) {
      console.error('RevenueCat Project ID not found');
      return null;
    }
    const fn = httpsCallable<{ userId?: string; projectId: string }, RevenueCatOfferingsResponse>(
      functions, 'revenueCatGetOfferings'
    );
    const result = await fn({ userId, projectId });
    console.log('Offerings fetched successfully:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error fetching offerings:', error);
    return null;
  }
};

export const purchasePackage = async (userId: string, productIdentifier: string) => {
  try {
    const fn = httpsCallable<{ userId: string; productIdentifier: string }, any>(
      functions, 'revenueCatPurchase'
    );
    const result = await fn({ userId, productIdentifier });
    return result.data;
  } catch (error) {
    console.error('Error purchasing package:', error);
    throw error;
  }
};

export const getCustomerInfo = async (userId: string): Promise<RevenueCatSubscriber | null> => {
  try {
    const fn = httpsCallable<{ userId: string }, { subscriber: RevenueCatSubscriber | null }>(
      functions, 'revenueCatGetCustomerInfo'
    );
    const result = await fn({ userId });
    return result.data.subscriber;
  } catch (error) {
    console.error('Error fetching customer info:', error);
    return null;
  }
};

// Check if user has an active subscription from any platform
export const hasActiveSubscription = (subscriber: RevenueCatSubscriber | null): boolean => {
  if (!subscriber) return false;

  // Check entitlements for active subscriptions
  const entitlements = subscriber.entitlements || {};
  const now = new Date();

  for (const entitlementKey in entitlements) {
    const entitlement = entitlements[entitlementKey];
    const expiresDate = new Date(entitlement.expires_date);

    // Check if entitlement is still active
    if (expiresDate > now) {
      return true;
    }
  }

  return false;
};

// Get active subscription details
export const getActiveSubscriptionDetails = (subscriber: RevenueCatSubscriber | null) => {
  if (!subscriber) return null;

  const entitlements = subscriber.entitlements || {};
  const subscriptions = subscriber.subscriptions || {};
  const now = new Date();

  // Find first active entitlement
  for (const entitlementKey in entitlements) {
    const entitlement = entitlements[entitlementKey];
    const expiresDate = new Date(entitlement.expires_date);

    if (expiresDate > now) {
      // Get subscription details - try product_identifier first, then check all subscriptions
      let subscription = subscriptions[entitlement.product_identifier];

      // If not found by product_identifier, find the first active subscription from Stripe
      if (!subscription) {
        console.log('Subscription not found by product_identifier, searching all subscriptions...');
        for (const subKey in subscriptions) {
          const sub = subscriptions[subKey];
          if (sub.store === 'stripe' || sub.store === 'promotional') {
            subscription = sub;
            console.log('Found subscription by store type:', subKey);
            break;
          }
        }
      }

      console.log('Active subscription found:', {
        productIdentifier: entitlement.product_identifier,
        store: subscription?.store,
        store_transaction_id: subscription?.store_transaction_id,
        subscriptionKeys: Object.keys(subscriptions),
        fullSubscription: subscription
      });

      return {
        productIdentifier: entitlement.product_identifier,
        displayName: subscription?.display_name || entitlement.product_identifier,
        expiresDate: entitlement.expires_date,
        purchaseDate: entitlement.purchase_date,
        store: subscription?.store || 'unknown',
        periodType: subscription?.period_type || 'normal',
        willRenew: !subscription?.unsubscribe_detected_at,
        price: subscription?.price,
        stripeSubscriptionId: subscription?.store === 'stripe' ? subscription?.store_transaction_id : null,
        isInTrial: subscription?.period_type === 'trial',
        billingIssuesDetectedAt: subscription?.billing_issues_detected_at,
      };
    }
  }

  return null;
};
